import { ApiFeaturedListResponse } from "shared/types/selection/ApiSelectionFeaturedList";
import { BaseSystemGeneratedList } from "./Base";
import { FeaturedSelectionFormFields } from "../../utils";
import isNumber from "lodash/isNumber";
import SelectableType from "shared/enums/SelectableType";
import { processCriteriaForQueryBuilder } from "../searchCriteria";
import * as yup from "yup";
import { Copies } from "shared/hooks/useCopies";
import Validation from "shared/form/Validation";
import { getYupTestForQuery } from "../../service";
import FeaturedListMatchCriteriaType from "../../../../../../../../../../shared/enums/FeaturedListMatchCriteriaType";
import ContextEntityType from "shared/enums/ContextEntityType";
import FeaturedListType from "shared/enums/FeaturedListType";

export default class SelectionRecipientSystemGeneratedList extends BaseSystemGeneratedList {
  getInitialValues(contextId: string, contextEntityType: ContextEntityType, data?: ApiFeaturedListResponse) {
    return {
      id: data?.id || null,
      [FeaturedSelectionFormFields.DISPLAY_LABEL]: data?.displayLabel || "",
      [FeaturedSelectionFormFields.DESCRIPTION]: data?.description || "",
      [FeaturedSelectionFormFields.DISPLAY_ORDER]: data?.displayOrder?.toString() || "",
      [FeaturedSelectionFormFields.AVAILABILITY_TYPES]: data?.availabilityTypes || [],
      [FeaturedSelectionFormFields.SECONDARY_FILTERS]: data?.secondaryFilters || [],
      [FeaturedSelectionFormFields.CONTEXT_ID]: data?.contextId || contextId,
      [FeaturedSelectionFormFields.CONTEXT_ENTITY_TYPE]: data?.contextEntityType || contextEntityType,
      [FeaturedSelectionFormFields.SELECTABLE_CARD_CONFIGURATION_ID]: data?.selectableCardConfiguration?.id || "",
      [FeaturedSelectionFormFields.GROUP_BY_CARD_CONFIGURATION_ID]:
        data?.[FeaturedSelectionFormFields.SELECTABLE_TYPE] === SelectableType.PARTICIPANT
          ? data?.groupByCardConfiguration?.id || ""
          : "",
      [FeaturedSelectionFormFields.SHORT_DISPLAY_LABEL]: data?.shortDisplayLabel || "",
      [FeaturedSelectionFormFields.RECIPIENT_MATCH_CRITERIA_TYPE]:
        data?.recipientMatchCriteriaType || FeaturedListMatchCriteriaType.NONE,
      [FeaturedSelectionFormFields.GROUP_BY_TYPE]:
        data?.[FeaturedSelectionFormFields.SELECTABLE_TYPE] === SelectableType.PARTICIPANT ? data?.groupByType : "",
      [FeaturedSelectionFormFields.UI_VERSION]: isNumber(data?.uiVersion) ? data?.uiVersion : null,
      [FeaturedSelectionFormFields.RECIPIENT_FIELD_MAPPING]: data?.recipientFieldMapping || null,
      [FeaturedSelectionFormFields.TYPE]: data?.type || "",
      [FeaturedSelectionFormFields.TAGS]: data?.tags || [],
      [FeaturedSelectionFormFields.SELECTABLE_TYPE]: data?.selectableType || "",
      actorCriteria: data?.actorCriteria ? processCriteriaForQueryBuilder(data.actorCriteria) : null,
      recipientCriteria: data?.recipientCriteria ? processCriteriaForQueryBuilder(data.recipientCriteria) : null
    };
  }
  getValidationSchema(copies: Copies) {
    return yup.object().shape({
      [FeaturedSelectionFormFields.TYPE]: Validation.textFieldValidation({ copies, required: true }),
      [FeaturedSelectionFormFields.DISPLAY_LABEL]: Validation.textFieldValidation({ copies, required: true }),
      [FeaturedSelectionFormFields.SHORT_DISPLAY_LABEL]: Validation.textFieldValidation({ copies, required: true }),
      [FeaturedSelectionFormFields.DESCRIPTION]: Validation.textFieldValidation({ copies, required: false }),
      [FeaturedSelectionFormFields.DISPLAY_ORDER]: Validation.numberValidation({ copies, required: true }),
      [FeaturedSelectionFormFields.AVAILABILITY_TYPES]: Validation.multiSelectValidation({ copies, required: true }),
      [FeaturedSelectionFormFields.SECONDARY_FILTERS]: Validation.multiSelectValidation({ copies, required: true }),
      [FeaturedSelectionFormFields.GROUP_BY_TYPE]: Validation.textFieldValidation({ copies, required: false }).when(
        FeaturedSelectionFormFields.SELECTABLE_TYPE,
        {
          is: SelectableType.PARTICIPANT,
          then: Validation.textFieldValidation({ copies, required: true })
        }
      ),
      [FeaturedSelectionFormFields.GROUP_BY_CARD_CONFIGURATION_ID]: Validation.textFieldValidation({
        copies,
        required: false
      }).when(FeaturedSelectionFormFields.SELECTABLE_TYPE, {
        is: SelectableType.PARTICIPANT,
        then: Validation.textFieldValidation({ copies, required: true })
      }),
      [FeaturedSelectionFormFields.SELECTABLE_CARD_CONFIGURATION_ID]: Validation.textFieldValidation({
        copies,
        required: true
      }),
      [FeaturedSelectionFormFields.RECIPIENT_MATCH_CRITERIA_TYPE]: Validation.textFieldValidation({
        copies,
        required: true
      }),
      [FeaturedSelectionFormFields.ACTOR_CRITERIA]: yup
        .object()
        .nullable()
        .test("actorCriteriaTest", getYupTestForQuery(copies, { isOptional: true })),
      [FeaturedSelectionFormFields.RECIPIENT_CRITERIA]: yup
        .object()
        .nullable()
        .when(FeaturedSelectionFormFields.RECIPIENT_MATCH_CRITERIA_TYPE, {
          is: FeaturedListMatchCriteriaType.GENERIC,
          then: yup
            .object()
            .nullable()
            .test("recepientCriteriaTest", getYupTestForQuery(copies, { isOptional: false }))
        }),
      [FeaturedSelectionFormFields.RECIPIENT_FIELD_MAPPING]: yup
        .object()
        .nullable()
        .when(FeaturedSelectionFormFields.RECIPIENT_MATCH_CRITERIA_TYPE, {
          is: FeaturedListMatchCriteriaType.FIELD_MAPPING,
          then: yup
            .object()
            .nullable()
            .test("fieldMappingTest", copies.get("required"), function (fieldMapping: any) {
              return fieldMapping?.fieldOptionMappings?.length > 0;
            })
        }),
      [FeaturedSelectionFormFields.TAGS]: yup
        .array()
        .nullable()
        .when(FeaturedSelectionFormFields.TYPE, {
          is: FeaturedListType.DEFAULT,
          then: Validation.yupIsReqArr({
            copies,
            required: true
          }),
          otherwise: Validation.yupIsReqArr({
            copies,
            required: false
          })
        })
    });
  }
}
